// src/pages/cashier/CashierOrderItemSelector.tsx
import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useCashierStore } from "../../store/cashier.store";
import { CartItem, MenuItem, Kitchen } from "../../types";
import toast from "react-hot-toast";

interface Props {
  draftId: string;
  onItemsSelected: () => void;
  onBack: () => void;
}

type SelectorView = "kitchen-browse" | "kitchen-menu" | "cart-review";

const CashierOrderItemSelector: React.FC<Props> = ({
  draftId,
  onItemsSelected,
  onBack,
}) => {
  const [view, setView] = useState<SelectorView>("kitchen-browse");
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [selectedKitchen, setSelectedKitchen] = useState<Kitchen | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemQty, setSelectedItemQty] = useState<Record<string, number>>({});

  const { pipelineOrders, updateDraftItems } = useCashierStore();
  const draft = pipelineOrders.find((o) => o.id === draftId);

  useEffect(() => {
    fetchKitchens();
  }, []);

  const fetchKitchens = async () => {
    try {
      setLoading(true);
      // Kitchen docs carry status ("active" / "paused" / "blocked"), not isActive.
      const snap = await getDocs(collection(db, "kitchens"));
      const data = snap.docs
        .map((d) => ({ ...d.data(), uid: d.id, id: d.id }) as any)
        .filter((k) => k.status !== "blocked" && k.isActive !== false) as Kitchen[];
      setKitchens(data);
    } catch (error) {
      console.error("Error fetching kitchens:", error);
      toast.error("Failed to load kitchens");
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (kitchenId: string) => {
    try {
      setLoading(true);
      // Live menu: menus/{kitchenId}.menuItems (the old top-level "menuItems" collection is empty).
      // The map key is sent as id/menuItemId so cashierPlaceOrder prices it from the same menu entry.
      const menuSnap = await getDoc(doc(db, "menus", kitchenId));
      const entries = Object.entries(((menuSnap.data() || {}).menuItems || {}) as Record<string, any>);
      const data = entries
        .filter(([, it]) => it && it.isAvailable !== false && !it.deleted && !it.isDeleted)
        .map(([key, it]) => ({
          ...it,
          id: key,
          menuItemId: key,
          kitchenId,
          price: Number(it.price ?? it.basePrice ?? 0),
        })) as unknown as MenuItem[];
      setMenuItems(data);
      setSelectedItemQty({});
    } catch (error) {
      console.error("Error fetching menu:", error);
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  const handleKitchenSelect = async (kitchen: Kitchen) => {
    setSelectedKitchen(kitchen);
    await fetchMenuItems(kitchen.uid || kitchen.id!);
    setView("kitchen-menu");
  };

  const handleAddItem = (item: MenuItem) => {
    if (!selectedKitchen || !draft) return;

    const qty = selectedItemQty[item.id] || 1;

    const cartItem: CartItem = {
      id: item.id,
      menuItemId: item.id,
      name: item.name,
      price: item.price,
      category: item.category,
      quantity: qty,
      photoUrl: item.photoUrl,
      kitchenId: selectedKitchen.uid || selectedKitchen.id!,
      kitchenName: selectedKitchen.kitchen_name || "Unknown",
      description: item.description,
    };

    const existingIndex = draft.items.findIndex((ci) => ci.id === item.id);
    let newItems = [...draft.items];

    if (existingIndex >= 0) {
      newItems[existingIndex].quantity += qty;
    } else {
      newItems.push(cartItem);
    }

    const newTotal = newItems.reduce(
      (sum, ci) => sum + ci.price * ci.quantity,
      0
    );

    updateDraftItems(draftId, newItems, newTotal);
    toast.success(`${item.name} added!`);
    setSelectedItemQty({ ...selectedItemQty, [item.id]: 1 });
  };

  const handleRemoveItem = (itemId: string) => {
    if (!draft) return;

    const newItems = draft.items.filter((ci) => ci.id !== itemId);
    const newTotal = newItems.reduce(
      (sum, ci) => sum + ci.price * ci.quantity,
      0
    );

    updateDraftItems(draftId, newItems, newTotal);
    toast.success("Item removed");
  };

  if (!draft) {
    return <div className="text-center p-8 text-white">Loading...</div>;
  }

  // Kitchen Browse
  if (view === "kitchen-browse") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-white">🏪 Select Kitchen</h1>
            <button
              onClick={onBack}
              className="text-slate-300 hover:text-white"
            >
              ← Back
            </button>
          </div>

          {loading ? (
            <div className="text-center text-white">Loading kitchens...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kitchens.map((kitchen) => (
                <button
                  key={kitchen.id}
                  onClick={() => handleKitchenSelect(kitchen)}
                  className="bg-slate-700 hover:bg-slate-600 rounded-lg p-4 text-left transition-colors"
                >
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {kitchen.kitchen_name}
                  </h3>
                  <p className="text-sm text-slate-300 mb-3">
                    📍 {kitchen.address || "Location TBD"}
                  </p>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">
                      Min: ₹{kitchen.minOrder || "0"}
                    </span>
                    <span className="bg-blue-600 text-white px-3 py-1 rounded text-xs">
                      Select
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {draft.items.length > 0 && (
            <button
              onClick={() => setView("cart-review")}
              className="fixed bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg"
            >
              🛒 Review Cart ({draft.items.length})
            </button>
          )}
        </div>
      </div>
    );
  }

  // Menu Items
  if (view === "kitchen-menu" && selectedKitchen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-white">
              🍽️ {selectedKitchen.kitchen_name}
            </h1>
            <button
              onClick={() => setView("kitchen-browse")}
              className="text-slate-300 hover:text-white"
            >
              ← Back
            </button>
          </div>

          {loading ? (
            <div className="text-center text-white">Loading menu...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {menuItems.map((item) => (
                <div key={item.id} className="bg-slate-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {item.name}
                  </h3>
                  <p className="text-sm text-slate-300 mb-3">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-white">
                      ₹{item.price}
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={selectedItemQty[item.id] || 1}
                      onChange={(e) =>
                        setSelectedItemQty({
                          ...selectedItemQty,
                          [item.id]: parseInt(e.target.value),
                        })
                      }
                      className="w-16 bg-slate-600 text-white rounded px-2 py-1 text-center"
                    />
                  </div>
                  <button
                    onClick={() => handleAddItem(item)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded"
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          )}

          {draft.items.length > 0 && (
            <button
              onClick={() => setView("cart-review")}
              className="fixed bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg"
            >
              🛒 Review ({draft.items.length})
            </button>
          )}
        </div>
      </div>
    );
  }

  // Cart Review
  if (view === "cart-review") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-white">🛒 Cart Review</h1>
            <button
              onClick={() => setView("kitchen-menu")}
              className="text-slate-300 hover:text-white"
            >
              ← Back
            </button>
          </div>

          <div className="bg-slate-700 rounded-lg p-6 mb-6">
            {draft.items.map((item) => (
              <div
                key={item.id}
                className="flex justify-between items-center py-3 border-b border-slate-600 last:border-b-0"
              >
                <div>
                  <p className="font-semibold text-white">{item.name}</p>
                  <p className="text-sm text-slate-400">
                    {item.kitchenName} • {item.quantity}x
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-bold text-white">
                    ₹{(item.price * item.quantity).toLocaleString()}
                  </span>
                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}

            <div className="mt-6 pt-6 border-t border-slate-600">
              <div className="flex justify-between mb-2">
                <span className="text-slate-300">Subtotal</span>
                <span className="text-white font-semibold">
                  ₹{draft.totalAmount}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              onItemsSelected();
            }}
            disabled={draft.items.length === 0}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-lg disabled:opacity-50"
          >
            Proceed to Payment
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default CashierOrderItemSelector;
